import { reactive } from 'vue';

/**
 * Tiny Sonner-style toast store — a module-level singleton so any component can
 * push a toast and the single <Toaster /> instance renders them.
 *
 *   const toast = useToast();
 *   toast.success('Reply ready');
 *   toast.error('Message failed', { duration: 8000 });
 *
 * Toasts auto-dismiss after `duration` ms (default 5000). Pass duration: 0 to
 * make a toast sticky. Kept dependency-free and inside the widget's token scope
 * so it themes with the active variant and never leaks onto the host page.
 */
const DEFAULT_DURATION = 5000;

let seq = 0;
const state = reactive({ toasts: [] });
const timers = new Map();

function dismiss(id) {
    const index = state.toasts.findIndex((toast) => toast.id === id);
    if (index !== -1) state.toasts.splice(index, 1);
    const timer = timers.get(id);
    if (timer) {
        clearTimeout(timer);
        timers.delete(id);
    }
}

function push(type, message, opts = {}) {
    const id = ++seq;
    state.toasts.push({ id, type, message });

    const duration = opts.duration ?? DEFAULT_DURATION;
    if (duration > 0 && typeof window !== 'undefined') {
        timers.set(id, window.setTimeout(() => dismiss(id), duration));
    }
    return id;
}

export function useToast() {
    return {
        toasts: state.toasts,
        success: (message, opts) => push('success', message, opts),
        error: (message, opts) => push('error', message, opts),
        info: (message, opts) => push('info', message, opts),
        dismiss,
    };
}
